import {
    EXCHANGE_POLICY,
    REFUND_OPTIONS,
    RETURN_METHOD_COMPLAINT,
    RETURN_METHOD_EXCHANGE,
    RETURN_METHOD_RESALE,
    RETURN_METHOD_RETURN,
    ROUTES,
    WITHDRAWAL_ITEM_SELECTION_MODES,
    WITHDRAWAL_SHIPMENT_MODES,
} from "../constants";
import { format, subDays } from "date-fns";
import Holidays from "date-holidays";
import { getSelectedProductProductId, isComplaintSelected, isExchangeSelected, isResaleSelected, isReturnSelected } from "./selectedProductValuesCore";
import { MAINTENANCE_ACTIVE } from "rylak-common/src/constants";
import _ from "lodash";
import { isProductComplaintDisabled } from "./productCore";

export function isInMaintenance(appProps) {
    return MAINTENANCE_ACTIVE && appProps?.initServiceData?.status === 404;
}

export function getLoginServiceData(appProps) {
    return appProps?.loginServiceData?.data;
}

export function getProductsExchangeServiceData(appProps) {
    return appProps?.productsExchangeServiceData;
}

export function getProductExchangeProducts(appProps, productId) {
    return getProductsExchangeServiceData(appProps)?.[productId]?.data?.products;
}

export function getOrderCreatedAt(appProps) {
    return getLoginServiceData(appProps)?.orderCreatedAt;
}

export function getOrderTotalAmount(appProps) {
    return getLoginServiceData(appProps)?.totalAmount;
}

export function getRemainingReturnDays(appProps) {
    return getLoginServiceData(appProps)?.remainingReturnDays;
}

export function getRemainingWithdrawalDays(appProps) {
    return getLoginServiceData(appProps)?.remainingWithdrawalDays;
}

export function isWithdrawalActive(appProps) {
    return appProps?.withdrawal === true;
}

// How items are pre-selected in the withdrawal flow. Defaults to WHOLE_ORDER_LOCKED (all items selected and not
// deselectable) when the backend hasn't sent a mode yet.
export function getWithdrawalItemSelectionMode(appProps) {
    return getLoginServiceData(appProps)?.withdrawalItemSelectionMode || WITHDRAWAL_ITEM_SELECTION_MODES.WHOLE_ORDER_LOCKED;
}

// Whether all items should start selected when entering the withdrawal flow.
export function shouldPreselectWithdrawalItems(appProps) {
    const mode = getWithdrawalItemSelectionMode(appProps);
    return mode === WITHDRAWAL_ITEM_SELECTION_MODES.PRESELECT_ALL || mode === WITHDRAWAL_ITEM_SELECTION_MODES.WHOLE_ORDER_LOCKED;
}

// Whether the customer is prevented from deselecting items (the "whole order, locked" mode).
export function isWithdrawalSelectionLocked(appProps) {
    return isWithdrawalActive(appProps) && getWithdrawalItemSelectionMode(appProps) === WITHDRAWAL_ITEM_SELECTION_MODES.WHOLE_ORDER_LOCKED;
}

// Shipment handling for the withdrawal flow. Defaults to ACKNOWLEDGMENT_ONLY when the backend hasn't sent a mode.
export function getWithdrawalShipmentMode(appProps) {
    return getLoginServiceData(appProps)?.withdrawalShipmentMode || WITHDRAWAL_SHIPMENT_MODES.ACKNOWLEDGMENT_ONLY;
}

// Acknowledgment-only withdrawals are refunded as BASIC; an "offer shipping" withdrawal behaves like a regular
// return, so its refund options follow the price setup.
export function isWithdrawalAcknowledgmentOnly(appProps) {
    return getWithdrawalShipmentMode(appProps) === WITHDRAWAL_SHIPMENT_MODES.ACKNOWLEDGMENT_ONLY;
}

export function getProductsData(appProps) {
    return getLoginServiceData(appProps)?.products || getLoginServiceData(appProps)?.orders.flatMap((o) => o.products) || [];
}

export function getProductVariantBySessionItemId(appProps, sessionItemId) {
    return getProductsData(appProps).find((prod) => prod.sessionItemId == sessionItemId);
}

export function getProductVariantById(appProps, productVariantId) {
    return getProductsData(appProps).find((prod) => prod.productVariantId == productVariantId);
}

export function getSelectedExchangeVariant(appProps, productId, exchangeToProductId, exchangeAlternativeId) {
    const foundExchangeToProduct = getProductExchangeProducts(appProps, productId)?.find((product) => product?.id === exchangeToProductId);
    return foundExchangeToProduct?.variants?.find((variant) => variant?.id === exchangeAlternativeId);
}

export function findSelectedProductValues(appProps, productId) {
    return getSelectedProductValues(appProps)?.find((v) => v.productId === productId) || null;
}

export function removeSelectedProductValues(appProps, productId) {
    return getSelectedProductValues(appProps)?.filter((v) => v.productId !== productId);
}

export function updateSelectedProductValues(appProps, productId, data) {
    const newValues = removeSelectedProductValues(appProps, productId);
    if (data) {
        newValues.push(data);
    }
    return newValues;
}

export function isAnyComplaintSelected(appProps) {
    return isReturnOfTypeSelected(appProps, RETURN_METHOD_COMPLAINT);
}

export function isOnlyComplaintSelected(appProps) {
    return isAnyComplaintSelected(appProps) && !isAnyReturnSelected(appProps) && !isAnyExchangeSelected(appProps) && !isAnyResaleSelected(appProps);
}

export function isOnlyNoReturnComplaintsSelected(appProps) {
    return (
        isOnlyComplaintSelected(appProps) &&
        getSelectedProductValues(appProps)?.every((selectedProductValues) => {
            const complaintReason = selectedProductValuesComplaintReason(appProps, selectedProductValues);
            return complaintReason?.isNoReturn;
        })
    );
}

export function isAnyResaleSelected(appProps) {
    return isReturnOfTypeSelected(appProps, RETURN_METHOD_RESALE);
}

export function isAnyReturnSelected(appProps) {
    return isReturnOfTypeSelected(appProps, RETURN_METHOD_RETURN);
}

export function isAnyExchangeSelected(appProps) {
    return isReturnOfTypeSelected(appProps, RETURN_METHOD_EXCHANGE);
}

export function getSelectedProductValues(appProps) {
    return appProps?.selectedProductsValues;
}

export function isReturnOfTypeSelected(appProps, returnMethod) {
    return !!getSelectedProductValues(appProps)?.find((productValues) => {
        if (returnMethod === RETURN_METHOD_COMPLAINT && isComplaintSelected(productValues)) {
            return true;
        }
        if (returnMethod === RETURN_METHOD_RETURN && isReturnSelected(productValues)) {
            return true;
        }
        if (returnMethod === RETURN_METHOD_EXCHANGE && isExchangeSelected(productValues)) {
            return true;
        }

        if (returnMethod === RETURN_METHOD_RESALE && isResaleSelected(productValues)) {
            return true;
        }

        return false;
    });
}

export function getProductSessionItemId(product) {
    return product.sessionItemId;
}

export function isAnySelectedProductFromResaleFlow(appProps) {
    return getSelectedProductsResaleFlowValues(appProps)?.length > 0;
}

export function isAnySelectedProductFromReturnFlow(appProps) {
    return getSelectedProductsResaleFlowValues(appProps)?.length > 0;
}

export function isAnySelectedProductFromResaleAndReturnFlow(appProps) {
    return isAnySelectedProductFromResaleFlow(appProps) && isAnySelectedProductFromReturnFlow(appProps);
}

export function getSelectedProductsReturnFlowValues(appProps) {
    return getSelectedProductValues(appProps).filter((spv) => !selectedProductValuesToProduct(appProps, spv).resaleActive);
}

export function getSelectedProductsResaleFlowValues(appProps) {
    return getSelectedProductValues(appProps)?.filter((spv) => selectedProductValuesToProduct(appProps, spv).resaleActive);
}

export function selectedProductValuesToProduct(appProps, selectedProductValues) {
    return getProductsData(appProps).find((p) => getProductSessionItemId(p) === getSelectedProductProductId(selectedProductValues));
}

export function selectedProductValuesComplaintReason(appProps, selectedProductValues) {
    const product = selectedProductValuesToProduct(appProps, selectedProductValues);
    if (isComplaintSelected(selectedProductValues)) {
        return product?.complaintReasons?.find((complainReason) => complainReason?.id === selectedProductValues?.complaintReasonId);
    }
    return null;
}

export function productToSelectedProductValues(appProps, product) {
    return getSelectedProductValues(appProps)?.find((selectedProductValues) => (product?.productId || product?.sessionItemId) === getSelectedProductProductId(selectedProductValues));
}

const holidays = new Holidays();
holidays.init("SE");

function isDisabledDate(date) {
    if (holidays.isHoliday(subDays(date, -1))) {
        return true;
    }
    let dayOfTheWeek = date.getDay();
    if (dayOfTheWeek === 5 || dayOfTheWeek === 6) {
        // Friday or Saturday
        return true;
    }
    let month = date.getMonth(),
        day = date.getDate();
    if (month === 11) {
        if ((day >= 23 && day <= 25) || day === 31) {
            return true;
        }
    } else if (month === 0) {
        if (day === 5) {
            return true;
        }
    }
    return false;
}

export const selectableDatesData = [];
let currentDate = new Date();
for (let i = 0; i < 14; i++) {
    const date = new Date();
    date.setDate(currentDate.getDate() + i + 3);
    selectableDatesData[i] = { date: date, disabled: isDisabledDate(date) };
}

export function findFirstAvailableDateIndex() {
    return selectableDatesData.findIndex((data) => !data.disabled);
}

export const formatToRegularDate = function (date) {
    return format(date, "yyyy-MM-dd");
};

export const newPickupSelectableDatesData = [];
for (let i = 0; i < 14; i++) {
    const date = new Date();
    date.setDate(currentDate.getDate() + i);
    newPickupSelectableDatesData[i] = {
        date: date,
        disabled: isDisabledDate(date),
    };
}

export function getPropertyValue({ product, propertyType }) {
    return product?.properties?.find((p) => p.type === propertyType)?.value;
}

export function resaleActive(appProps) {
    return getLoginServiceData(appProps)?.resaleActive;
}

export function getFilteredRefundOptions(appProps) {
    const complaintExists = isAnyComplaintSelected(appProps);
    const returnExists = isAnyReturnSelected(appProps);
    const resaleExists = isAnyResaleSelected(appProps);

    if (returnExists || resaleExists || complaintExists) {
        return getRefundOptions(appProps);
    }
    return null;
}

export function getRefundOptions(appProps) {
    return getLoginServiceData(appProps)?.refundOptions;
}

export function getSelectedRefundOption(appProps) {
    return appProps?.selectedRefundOption;
}

export function hasMultipleRefundOptions(appProps) {
    return getRefundOptions(appProps)?.length > 1;
}

export function isRefundOptionSelected(appProps) {
    // If we only have one refund options it's indirectly selected
    return !hasMultipleRefundOptions(appProps) || !!getSelectedRefundOption(appProps);
}

export function getSelectedRefundOptionObject(appProps) {
    const refundOptions = getFilteredRefundOptions(appProps);

    const selectedRefundOptionIndex = refundOptions?.findIndex((o) => o.type === getSelectedRefundOption(appProps));
    return refundOptions && refundOptions[selectedRefundOptionIndex];
}

export function getNumberOfExchanges({ orderReturn }) {
    return orderReturn?.numberOfExchanges;
}

export function getReturnReasonTranslated({ returnReason, language }) {
    return returnReason.translations[language] || returnReason.translations["EN"] || returnReason.translations[0] || "";
}

export function getAllAvailableVariantValuesOfType({ variantType, productExchangeServiceData }) {
    const originalProduct = productExchangeServiceData?.data?.products?.find((p) => p?.isOriginal);
    const propertyValues = originalProduct?.variants?.map(
        (v) =>
            v?.inventoryMerchant > 0 &&
            v?.properties?.find((vp) => {
                return vp?.type?.toLowerCase() === variantType;
            })?.value,
    );
    return _.uniq(propertyValues?.filter((v) => v));
}

export function getAllAvailableVariantColors({ productExchangeServiceData }) {
    return getAllAvailableVariantValuesOfType({ variantType: "color", productExchangeServiceData });
}

export function getAllAvailableVariantSizes({ productExchangeServiceData }) {
    return getAllAvailableVariantValuesOfType({ variantType: "size", productExchangeServiceData });
}

export function getAlternativeProductsOfVariantType({ product, variantType, productExchangeServiceData }) {
    const productVariants = productExchangeServiceData?.data?.products?.find((p) => p?.isOriginal)?.variants;
    const propertiesExceptCurrent = product?.properties?.filter((p) => p?.type.toLowerCase() !== variantType?.toLowerCase());

    return productVariants?.filter(
        (v) =>
            v?.isOriginal === false &&
            v?.inventoryMerchant > 0 &&
            propertiesExceptCurrent?.every((property) => {
                const propertyValue = v?.properties?.find((vp) => vp?.type === property.type)?.value;
                return !propertyValue || propertyValue === property?.value;
            }),
    );
}

export function getRecommendedExchangeProducts({ returnMethodData, product, productExchangeServiceData }) {
    if (isReturnSelected(returnMethodData)) {
        const returnReasons = returnMethodData?.returnReasons;
        if (returnReasons?.length === 1) {
            const returnReason = product?.returnReasonConfig?.returnReasons?.find((rr) => rr?.id === returnReasons[0]?.id);

            const englishTranslation = returnReason?.translations?.["EN"];
            const containsSize = englishTranslation?.toLowerCase()?.includes("size") || englishTranslation?.toLowerCase()?.includes("fit");
            const containsColor = englishTranslation?.toLowerCase()?.includes("color");

            if (containsSize || containsColor) {
                let result = [];
                if (containsSize) {
                    result = getAlternativeProductsOfVariantType({
                        product,
                        variantType: "size",
                        productExchangeServiceData,
                    });
                }
                if (containsColor) {
                    result = getAlternativeProductsOfVariantType({
                        product,
                        variantType: "color",
                        productExchangeServiceData,
                    });
                }

                return result;
            }
        }
    }
    return [];
}

export function isReturnTimeExceeded(appProps) {
    return appProps?.returnTimeExceeded;
}

export function isConfirmPage(appProps) {
    return appProps?.router.pathname === ROUTES.CONFIRMATION_PAGE.pathname;
}

export function isProductSelectionPage(appProps) {
    return appProps?.router.pathname === ROUTES.RETURN_PAGE.pathname;
}

export function isLoginPage(appProps) {
    return appProps?.router.pathname === ROUTES.MAIN_BRAND_PATH.pathname;
}

export function isSuccessPage(appProps) {
    return appProps?.router.pathname === ROUTES.SUCCESS_PAGE.pathname;
}

export function getExchangePolicy(appProps) {
    return getLoginServiceData(appProps).shopExchangePolicy;
}

export function getExchangePolicyTextKey(appProps) {
    if (isAnyExchangeSelected(appProps)) {
        if (getExchangePolicy(appProps) === EXCHANGE_POLICY.EXCHANGE_ON_RETURN) {
            return "confirmation-page.EXCHANGE_ON_RETURN";
        }
        if (getExchangePolicy(appProps) === EXCHANGE_POLICY.EXCHANGE_ON_SHIPMENT) {
            return "confirmation-page.EXCHANGE_ON_SHIPMENT";
        }
        if (getExchangePolicy(appProps) === EXCHANGE_POLICY.IMMEDIATE_EXCHANGE) {
            return null;
        }
        if (getExchangePolicy(appProps) === EXCHANGE_POLICY.MANUAL_APPROVAL) {
            return "confirmation-page.EXCHANGE_ON_RETURN";
        }
    }
    return null;
}

export function getCurrencyCode(appProps) {
    return appProps?.loginServiceData?.data?.currencyCode;
}

export function getGiftCardReturnOption(appProps) {
    return getLoginServiceData(appProps)?.refundOptions?.find((refundOption) => refundOption?.type === REFUND_OPTIONS.GIFT_CARD);
}

export function isGiftCardEnabled(appProps) {
    return !!getGiftCardReturnOption(appProps);
}

export function getGiftCardPercentage(appProps) {
    return getGiftCardReturnOption(appProps)?.bonusPercentage;
}

export function getProductsCombinedPurchasePrice(appProps) {
    return getProductsData(appProps)?.reduce((totalValue, product) => {
        return totalValue + product?.purchasePrice;
    }, 0);
}

export function isAppInIframe(appProps) {
    return appProps?.isInIframe;
}

export function isAppInitiatedWithQueryLanguage(appProps) {
    return appProps?.appInitiatedWithQueryLanguage;
}

export function isGlobalIframeModalActive(appProps) {
    return appProps?.globalIframeModalActive;
}

export function isComplaintsDisabledForAllItems(appProps) {
    return !getProductsData(appProps)?.find((product) => !isProductComplaintDisabled(product, { appProps }));
}

export function getAppWidth(appProps) {
    return appProps?.appWidth;
}

export function getAppHeight(appProps) {
    return appProps?.appHeight;
}

export function getSelectedLanguageCode(appProps) {
    return appProps?.language;
}

export function getLocaleTextCarrier(appProps, { carrier, preKeys = [], postKeys = [], variables = {}, options }) {
    const getLocaleText = appProps?.getLocaleText;
    const keysWithCarrier = preKeys.concat(carrier).concat(postKeys);
    const defaultKeysOld = preKeys.concat("default").concat(postKeys);
    const defaultKeysOld2 = preKeys.concat(postKeys);
    const defaultKeys = preKeys.concat("CARRIER_DEFAULT").concat(postKeys);

    const actualVariables = { carrier, ...variables };

    return (
        getLocaleText(keysWithCarrier, actualVariables, options) ||
        getLocaleText(defaultKeysOld, actualVariables, options) ||
        getLocaleText(defaultKeysOld2, actualVariables, options) ||
        getLocaleText(defaultKeys, actualVariables, options)
    );
}
